'use client';

import { c as _c } from "react/compiler-runtime";
import type { Middleware, MPAInterceptedResponse, RequestContext, ResponseContext } from '@repo/api-gw-sdk';
import type { Middleware as LakehouseMiddleware, RequestContext as LakehouseRequestContext, ResponseContext as LakehouseResponseContext } from '@repo/lakehouse-sdk';
import { useSearchParams } from 'next/navigation';
import { useMemo, type PropsWithChildren } from 'react';
import { NonProjectDalContext } from '@/contexts/nonProjectDalContext';
import { nonProjectDalFactory } from '@/data/dal/dalFactory';
import { createHttpClient, refreshTokenIfNeeded } from '@/data/dal/httpClient';
import { createMpaMiddleware } from '@/data/dal/mpaMiddleware';
import { createConfig, createLakehouseConfig } from './api-config';
import { createAuthMiddleware, createSharedRefreshToken } from './sdkMiddleware';
import { useEnvironment } from './useEnvironment';
interface NonProjectDalProviderProps {
  // Injected from the UI layer so the contexts module stays free of any
  // dialog/UI imports. Building the handler requires WorkspaceProvider, which
  // mounts above this provider — see `useMpaInterceptHandler`.
  mpaInterceptHandler: (payload: MPAInterceptedResponse) => Promise<void>;
  // False under the `(public)` layout (login, forgot-password, etc.). The
  // token-refresh + force-logout-on-401 behavior only makes sense for an
  // authenticated session; on public pages a 401 is a rejected credential, not
  // an expired session, so leaving this on would bounce the user to /logout
  // instead of letting the form show the error.
  isAuthenticated: boolean;
}
export const NonProjectDalProvider = t0 => {
  const $ = _c(48);
  const {
    children,
    mpaInterceptHandler,
    isAuthenticated
  } = t0;
  const env = useEnvironment();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("serverFlags");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const serverFlags = t1;
  const useRefreshToken = isAuthenticated && (!(env.isDemo || env.isDev || env.isTest) || env.enableForceLogoutOn401);
  let t2;
  if ($[2] !== env.authToken || $[3] !== env.clientApiBaseUrl || $[4] !== env.updateAuthToken || $[5] !== useRefreshToken) {
    t2 = createSharedRefreshToken({
      authToken: env.authToken,
      baseUrl: env.clientApiBaseUrl,
      updateAuthToken: env.updateAuthToken,
      useRefreshToken,
      refreshTokenIfNeeded
    });
    $[2] = env.authToken;
    $[3] = env.clientApiBaseUrl;
    $[4] = env.updateAuthToken;
    $[5] = useRefreshToken;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const refreshToken = t2;
  let t3;
  if ($[7] !== refreshToken || $[8] !== serverFlags || $[9] !== useRefreshToken) {
    t3 = createAuthMiddleware(refreshToken, serverFlags, useRefreshToken);
    $[7] = refreshToken;
    $[8] = serverFlags;
    $[9] = useRefreshToken;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const authMiddleware = t3;
  let t4;
  if ($[11] !== mpaInterceptHandler) {
    t4 = createMpaMiddleware(mpaInterceptHandler);
    $[11] = mpaInterceptHandler;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const mpaMiddleware = t4;
  let t5;
  if ($[13] !== authMiddleware || $[14] !== mpaMiddleware) {
    t5 = [authMiddleware, mpaMiddleware];
    $[13] = authMiddleware;
    $[14] = mpaMiddleware;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const apiGatewayMiddleware = t5;
  let t6;
  if ($[16] !== refreshToken || $[17] !== serverFlags || $[18] !== useRefreshToken) {
    t6 = createAuthMiddleware(refreshToken, serverFlags, useRefreshToken);
    $[16] = refreshToken;
    $[17] = serverFlags;
    $[18] = useRefreshToken;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const authMiddleware_0 = t6;
  let t7;
  if ($[20] !== mpaInterceptHandler) {
    t7 = createMpaMiddleware(mpaInterceptHandler);
    $[20] = mpaInterceptHandler;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const mpaMiddleware_0 = t7;
  let t8;
  if ($[22] !== authMiddleware_0 || $[23] !== mpaMiddleware_0) {
    t8 = [authMiddleware_0, mpaMiddleware_0];
    $[22] = authMiddleware_0;
    $[23] = mpaMiddleware_0;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const lakehouseMiddleware = t8;
  let t9;
  if ($[25] !== apiGatewayMiddleware || $[26] !== env.authToken || $[27] !== env.clientApiBaseUrl) {
    t9 = createConfig(env.clientApiBaseUrl, env.authToken, apiGatewayMiddleware);
    $[25] = apiGatewayMiddleware;
    $[26] = env.authToken;
    $[27] = env.clientApiBaseUrl;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const configuration = t9;
  let t10;
  if ($[29] !== env.authToken || $[30] !== env.clientApiBaseUrl || $[31] !== lakehouseMiddleware) {
    t10 = createLakehouseConfig(env.clientApiBaseUrl, env.authToken, lakehouseMiddleware);
    $[29] = env.authToken;
    $[30] = env.clientApiBaseUrl;
    $[31] = lakehouseMiddleware;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const lakehouseConfiguration = t10;
  let t11;
  if ($[33] !== env || $[34] !== useRefreshToken) {
    t11 = createHttpClient(env.authToken, env.updateAuthToken, env, useRefreshToken);
    $[33] = env;
    $[34] = useRefreshToken;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  const httpClient = t11;
  const t12 = env.isDev || env.isTest;
  let t13;
  if ($[36] !== configuration || $[37] !== httpClient || $[38] !== t12) {
    t13 = nonProjectDalFactory(httpClient, configuration, t12);
    $[36] = configuration;
    $[37] = httpClient;
    $[38] = t12;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  const nonProjectDal = t13;
  let t14;
  if ($[40] !== configuration || $[41] !== httpClient || $[42] !== lakehouseConfiguration || $[43] !== nonProjectDal) {
    t14 = {
      nonProjectDal,
      httpClient,
      configuration,
      lakehouseConfiguration
    };
    $[40] = configuration;
    $[41] = httpClient;
    $[42] = lakehouseConfiguration;
    $[43] = nonProjectDal;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== children || $[46] !== t14) {
    t15 = <NonProjectDalContext.Provider value={t14}>{children}</NonProjectDalContext.Provider>;
    $[45] = children;
    $[46] = t14;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  return t15;
};